/**
 * The app's connection to Viko: typed API, hub socket, device signer, and `dispatch()` —
 * sign a command and deliver it over the socket (HTTP fallback, outbox while offline).
 */
import type { SignedCommand } from '@viko/crypto'
import { type CommandKind, type CommandPayload, EncryptedContent } from '@viko/protocol'
import { materialView, type TranscriptView } from '@viko/transcript-ui' // materialized-state
import { createContext, useContext, useSyncExternalStore } from 'react'
import { API_ORIGIN, SOFTWARE_VERSION } from '../env.js'
import { Api, ApiError, type Fetcher } from './api.js'
import { ApprovalTray } from './approval-tray.js'
import { listIndexedArtifacts, recordArtifactIndex } from './artifact-index.js' // screen-sweep
import { installBuildFeedback } from './build-feedback.js'
import { EncryptedUnavailable } from './content-errors.js' // screen-sweep
import { keysApi } from './content-keys-api.js'
import { ContentLock, type ContentLockSource, probeContent } from './content-lock.js'
import { decryptTranscriptBatch } from './content-pipeline.js'
import { setupPasskeyPrf } from './content-recovery.js'
import { BrowserContentSession } from './content-session.js'
import { loadDevice } from './device-key.js'
import { BrowserArtifacts, browserArtifactTransport } from './encrypted-artifacts.js'
import { BrowserEntities, entityTransport } from './encrypted-entities.js'
import { BrowserHistory } from './encrypted-history.js'
import { browserReportTransport, installPrivateReports } from './encrypted-reports.js' // c-reports
import { BrowserRuns } from './encrypted-runs.js'
import { grantProtectedRetry } from './grant-retry.js'
import { browserSocket, CommandTimeoutError, HubClient, type SocketFactory } from './hub.js'
import { passkeys } from './passkeys.js'
import { historyTransport, PrivateHistory } from './private-history.js'
import { installPrivateInbox } from './private-inbox.js'
import { installPrivateWork, PrivateWork } from './private-work.js'
import { MaterialCache } from './progress-snapshot.js' // materialized-state
import { meteredFetcher, RateGate } from './request-meter.js'
import { RunSnapshots } from './run-snapshots.js' // materialized-state
import { RunStore } from './run-store.js'
import { RunTitles, type Titled } from './run-titles.js' // live-activity
import { SECRET_APPROVAL_SUMMARY, secretApprovalRetry } from './secret-approval.js'
import {
  endSessionOnly,
  installSessionOnlyUnload,
  isSessionOnly,
  rememberSessionDevice,
  wipeSessionOnly,
} from './session-only.js'
import { CommandSigner, type SignOptions, type StepUpRequest } from './signer.js'
import type { CommandOutcome, PreparedCommand, SessionInfo } from './types.js'
import { unlockCeremony } from './unlock-ceremony.js'
import { type PendingFile, uploadAll } from './uploads.js'
import { type ReportedRunUsage, reportedUsageOf, withViewUsage } from './usage-accounting.js' // usage-title
import { ENTITY_SOURCE, mergeRuns } from './work-projection.js'

export interface StepUpBridge {
  /** Registered by the <StepUpPrompt/> component; resolves when the user taps Continue. */
  request: ((req: StepUpRequest) => Promise<void>) | null
}

/**
 * How long a settled signed-in session answer is reused by the private readers. 0: readers that
 * start together share one in-flight check, but every later read re-checks, so an account switch
 * elsewhere is seen at once (encrypted-runs.test.ts).
 */
export const SESSION_SHARE_MS = 0

export class Backend {
  readonly api: Api
  private sharedSession: { p: Promise<SessionInfo>; at?: number; generation: number } | undefined
  /** Forget the shared session answer (sign-out, auth lost). */
  clearSessionCache() {
    this.sharedSession = undefined
  }
  /** The session check private readers run before each read, shared for SESSION_SHARE_MS. */
  private guardSession(): Promise<SessionInfo> {
    const s = this.sharedSession
    if (
      s &&
      s.generation === this.accountGeneration &&
      (s.at === undefined || Date.now() - s.at < SESSION_SHARE_MS)
    )
      return s.p
    const entry: { p: Promise<SessionInfo>; at?: number; generation: number } = {
      p: this.api.session(),
      generation: this.accountGeneration,
    }
    this.sharedSession = entry
    entry.p.catch(() => {
      if (this.sharedSession === entry) this.sharedSession = undefined
    })
    return entry.p
  }
  readonly hub: HubClient
  readonly signer: CommandSigner
  readonly content: BrowserContentSession | undefined
  artifacts: BrowserArtifacts | undefined
  private privateRuns: BrowserRuns | undefined
  private privateHistory: PrivateHistory | undefined
  entities: BrowserEntities | undefined
  private privateWork: PrivateWork | undefined
  private async workForAccount() {
    await this.guardSession()
    if (!this.privateWork) throw new Error('Private work is locked')
    return this.privateWork
  }
  private runRevision = 0
  private readonly runListeners = new Set<() => void>()
  subscribeRuns = (listener: () => void) => {
    this.runListeners.add(listener)
    return () => this.runListeners.delete(listener)
  }
  runsVersion = () => this.runRevision
  // ---- g4-cost: cached run-usage summaries, refreshed off the list path ----
  private usageCache: {
    entities: unknown
    byRun: Map<string, ReportedRunUsage>
    at: number
    timer?: ReturnType<typeof setTimeout>
  } = { entities: undefined, byRun: new Map(), at: 0 }
  private usageSummaries(entities: unknown, machineIds: string[]): Map<string, ReportedRunUsage> {
    const c = this.usageCache
    if (c.entities !== entities) {
      if (c.timer) clearTimeout(c.timer)
      this.usageCache = { entities, byRun: new Map(), at: 0 }
    }
    const cache = this.usageCache
    if (!cache.timer && machineIds.length && Date.now() - cache.at > 30_000)
      cache.timer = setTimeout(() => {
        cache.timer = undefined
        cache.at = Date.now()
        void this.api
          .runUsages(machineIds)
          .then((found) => {
            if (this.usageCache !== cache) return
            const next = new Map<string, ReportedRunUsage>()
            for (const m of found)
              for (const s of m.runs) {
                const cur = next.get(s.runId)
                if (!cur || (cur.observedAt ?? 0) <= s.updatedAt)
                  next.set(s.runId, reportedUsageOf(s))
              }
            const changed = JSON.stringify([...next]) !== JSON.stringify([...cache.byRun])
            cache.byRun = next
            if (changed) this.changedRuns()
          })
          .catch(() => {})
      }, 500)
    return cache.byRun
  }
  // ---- end g4-cost ----
  private changedRuns() {
    this.runRevision++
    for (const listener of this.runListeners) listener()
  }
  private contentAccountId: string | undefined
  private accountGeneration = 0
  private readonly accountListeners = new Set<() => void>()
  subscribeContent = (listener: () => void) => {
    this.accountListeners.add(listener)
    return () => this.accountListeners.delete(listener)
  }
  contentVersion = () => this.accountGeneration
  private readonly transcripts = new Map<string, RunStore>()
  readonly stepUp: StepUpBridge = { request: null }
  /** passkey-budget WP3: risky commands wait here; one passkey approves everything pending. */
  readonly tray = new ApprovalTray((reqs) => this.signer.signTapped(reqs))
  deviceKeyId: string | undefined
  private sessionUnload: (() => void) | undefined

  /**
   * "Just this session" is live on this page: revoke the session device when the page goes away
   * (keepalive request; the hub ends the bound sign-in). Idempotent.
   */
  armSessionOnly(deviceKeyId: string) {
    if (this.sessionUnload || typeof window === 'undefined') return
    rememberSessionDevice(deviceKeyId)
    this.sessionUnload = installSessionOnlyUnload((id) => {
      void this.fetcher(`${API_ORIGIN}/api/devices/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        keepalive: true,
        headers: { Accept: 'application/json' },
      }).catch(() => {})
    }, deviceKeyId)
  }

  /** Is encrypted work readable in this browser (lib/content-lock.ts)? One answer app-wide. */
  readonly lock: ContentLock

  /** Every hub HTTP request: metered (this tab's request meter) and held while rate limited. */
  readonly fetcher: Fetcher
  readonly rateGate = new RateGate()
  private reports: { changed(): void } | undefined
  /** The hub answered `unlock/holders` as unknown (older hub): use `unlock/machines` only. */
  private noHolders = false

  private async unlockHolders() {
    if (this.noHolders) throw new Error('unlock/holders unavailable')
    try {
      return (await keysApi(this.api).unlockHolders()).holders
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 400)) this.noHolders = true
      throw e
    }
  }

  constructor(
    fetcher: Fetcher,
    socket: SocketFactory,
    encrypted = true,
    /** Mock mode's simulated lock; the real one derives from the content session. */
    lockSource?: ContentLockSource,
  ) {
    this.fetcher = meteredFetcher(fetcher, { gate: this.rateGate })
    this.api = new Api(this.fetcher, API_ORIGIN)
    this.content = encrypted ? new BrowserContentSession(this.api, passkeys) : undefined
    const content = this.content
    this.lock = new ContentLock(
      lockSource ??
        (content
          ? {
              probe: () => probeContent(content),
              // unlock-via-mac: ONE ceremony for the app lock and the content (PRF or a machine).
              ceremony: (onPhase) => unlockCeremony(content, { api: this.api, passkeys, onPhase }),
              machines: async () => (await keysApi(this.api).unlockMachines()).machines,
              holders: () => this.unlockHolders(),
              protect: () => setupPasskeyPrf(content),
            }
          : undefined),
    )
    if (this.content)
      this.api.runs = async () => {
        await this.guardSession()
        if (!this.privateRuns) throw new Error('Encrypted runs locked')
        const runs = this.privateRuns
        const history = this.privateHistory
        const rows = [...(await runs.list()), ...((await history?.list()) ?? [])].map((r) => ({
          ...r,
          agent: r.agent ?? undefined,
        }))
        if (runs !== this.privateRuns) throw new Error('Encrypted account changed')
        return rows.map((row) => {
          const view = this.runView(row.runId) // materialized-state
          return view?.lastSeq
            ? { ...row, agent: view.agent, status: view.status, headSeq: view.lastSeq }
            : row
        })
      }
    if (this.content) {
      const history = async () => {
        await this.guardSession()
        if (!this.privateHistory) throw new Error('Encrypted history locked')
        return this.privateHistory
      }
      this.api.projectHistory = async (projectId, q = {}) => ({
        runs: await (await history()).list(projectId, q),
      })
      this.api.resumeRun = async (runId, prompt) => (await history()).resume(runId, prompt)
      this.api.search = async (query, scope = {}) => (await history()).search(query, scope)
    }
    if (this.content) installPrivateWork(this.api, () => this.workForAccount())
    // ---- b-work-web: add runs another trusted device mirrored into encrypted entities ----
    if (this.content) {
      const observedRuns = this.api.runs
      this.api.runs = async () => {
        const seen = await observedRuns()
        const work = this.privateWork
        const mirrored = (await work?.entityRunRows().catch(() => [])) ?? []
        if (work !== this.privateWork) throw new Error('Encrypted account changed')
        return mergeRuns([...seen, ...mirrored]).map((row) => {
          const view = this.runView(row.runId) // materialized-state
          if (row.source !== ENTITY_SOURCE || !view?.lastSeq) return row
          // Our own authenticated transcript supersedes the other device's claim.
          const { source: _mirrored, ...verified } = row
          return { ...verified, agent: view.agent, status: view.status, headSeq: view.lastSeq }
        })
      }
    }
    // ---- end b-work-web ----
    // ---- c-reports: encrypted report readers ----
    if (this.content) {
      const content = this.content
      this.reports = installPrivateReports(this.api, {
        account: async () => {
          await this.guardSession()
          return this.contentAccountId
        },
        keyForEpoch: (accountId, epoch) => content.readKey(accountId, epoch),
        // ---- l-fresh: fresh-account empty state ----
        unprovisioned: (accountId) => content.unprovisioned(accountId),
        // ---- end l-fresh ----
        transport: browserReportTransport(
          (input, init) =>
            this.fetcher(
              typeof input === 'string' ? input : input instanceof URL ? input.href : input.url,
              init,
            ),
          API_ORIGIN,
        ),
      })
    }
    // ---- end c-reports ----
    // ---- h-lanes: encrypted build feedback routes to the owning workstream ----
    if (this.content) installBuildFeedback(this.api)
    // ---- end h-lanes ----
    // ---- g-inbox: encrypted asks decrypted here; report-derived ship/build cards ----
    if (this.content) {
      const content = this.content
      installPrivateInbox(this.api, {
        ready: async () => {
          await this.guardSession()
          return this.contentAccountId !== undefined
        },
        open: async (e) => {
          const accountId = e.context.accountId
          if (accountId !== this.contentAccountId) throw new Error('Encrypted account changed')
          await content.readKey(accountId, e.context.epoch)
          const [event] = await decryptTranscriptBatch(
            content.vault,
            accountId,
            e.context.runId,
            [e],
            1,
          )
          if (!event) throw new Error('missing decrypted event')
          return event
        },
      })
    }
    // ---- end g-inbox ----
    // ---- screen-sweep: encrypted artifact index; legacy-only lanes answer honestly ----
    if (this.content) {
      this.api.artifacts = async (q = {}) => {
        await this.guardSession()
        const entities = this.entities
        const accountId = this.contentAccountId
        if (!entities || !accountId) throw new Error('Encrypted artifacts locked')
        const runs = q.projectId ? await this.api.runs().catch(() => []) : []
        const rows = await listIndexedArtifacts(entities, accountId, q, runs)
        if (entities !== this.entities) throw new Error('Encrypted account changed')
        return rows
      }
      // The hub refuses these for encrypted accounts before reading the request; never send it.
      const unavailable = (feature: string) => async (): Promise<never> => {
        throw new EncryptedUnavailable(feature)
      }
      this.api.artifactUrl = unavailable('Signed artifact links')
      this.api.compareArtifacts = unavailable('Compare')
      this.api.pick = unavailable('Picks')
      this.api.askStatus = unavailable('Status questions')
    }
    // ---- end screen-sweep ----
    // ---- live-activity: short run titles (rename > agent title > task file H1 > prompt) ----
    {
      const titled = async <T extends Titled>(rows: T[]) => {
        const entities = this.entities
        const titles = entities
          ? await this.runTitles.load(entities).catch(() => new Map<string, string>())
          : new Map<string, string>()
        if (entities !== this.entities) throw new Error('Encrypted account changed')
        // ---- g4-cost: rows carry the daemon's encrypted run-usage summary ----
        const machineIds = [
          ...new Set(rows.map((r) => (r as { machineId?: string }).machineId)),
        ].filter((id): id is string => !!id)
        // Never blocks the list: cached summaries now, a deferred refresh that re-renders on change.
        const usageByRun = this.usageSummaries(entities, machineIds)
        // ---- end g4-cost ----
        return (
          this.runTitles
            .apply(
              rows,
              titles,
              (runId) => this.runView(runId)?.session.agentTitle, // materialized-state
            )
            // usage-title: rows show the same decrypted usage as the session header
            .map((r) => withViewUsage(r, this.runView(r.runId))) // materialized-state
            .map((r) => {
              const u = usageByRun.get(r.runId) // g4-cost
              return u ? { ...r, usageReport: u } : r
            })
        )
      }
      const listRuns = this.api.runs
      this.api.runs = async () => {
        const rows = await listRuns()
        // materialized-state: list rows read daemon snapshots (one batched read), never histories.
        void this.runSnapshots?.fetch(rows.map((r) => r.runId))
        return titled(rows)
      }
      const projectHistory = this.api.projectHistory
      this.api.projectHistory = async (projectId, q) => {
        const out = await projectHistory(projectId, q)
        return { ...out, runs: await titled(out.runs) }
      }
    }
    // ---- end live-activity ----
    // burst-guard: every private reader re-checks the session before reading; readers that start
    // together share one in-flight check (`guardSession`). Direct `api.session()` calls always ask
    // the hub (sign-in flows, account switches) and refresh the shared answer.
    const session = this.api.session
    this.api.session = async () => {
      const generation = this.accountGeneration
      const result = await session()
      if (generation !== this.accountGeneration && result.userId !== this.contentAccountId) {
        if (this.sharedSession?.at === undefined) this.sharedSession = undefined
        return { authenticated: false }
      }
      this.bindAccount(result.authenticated && result.scope === 'full' ? result.userId : undefined)
      this.sharedSession =
        result.authenticated && result.scope === 'full'
          ? { p: Promise.resolve(result), at: Date.now(), generation: this.accountGeneration }
          : undefined
      return result
    }
    const logout = this.api.logout
    this.api.logout = async () => {
      // "Just this session": revoke the session device first (ends its sign-in at the hub), then
      // wipe the memory-only keys.
      if (isSessionOnly()) {
        this.sessionUnload?.()
        this.sessionUnload = undefined
        await endSessionOnly(this.api, this.deviceKeyId)
      }
      this.accountGeneration++
      this.clearSessionCache()
      this.hub.disconnect()
      this.bindAccount(undefined)
      return logout()
    }

    this.hub = new HubClient({
      ...(this.content
        ? { encryptedEvents: { accountId: () => this.content!.accountId(), vault: this.content } }
        : {}),
      socket,
      deviceId: () => this.deviceKeyId,
      softwareVersion: SOFTWARE_VERSION,
    })
    // burst-guard: a report broadcast makes the next projections list afresh (registered before
    // useHubSync's invalidations, so the refetches it triggers see the change).
    this.hub.onWork((f) => {
      if (f.entity === 'machine' || f.entity === 'landing' || f.entity === 'build')
        this.reports?.changed()
    })
    // ---- materialized-state: daemon snapshot pushes keep list surfaces and open stores fresh ----
    this.hub.onSnapshot((raw) => void this.runSnapshots?.push(raw))
    // ---- end materialized-state ----
    this.signer = new CommandSigner({
      device: () => loadDevice(),
      ...(this.content
        ? { content: (machineId: string) => this.content!.currentKey(machineId) }
        : {}),
      passkeys,
      confirmStepUp: (req) => (this.stepUp.request ? this.stepUp.request(req) : Promise.resolve()),
      tray: this.tray,
    })
  }

  private bindAccount(accountId: string | undefined) {
    if (this.contentAccountId === accountId) return
    // A "Just this session" tab whose sign-in the hub ended (removed on another browser, expired):
    // its memory-only keys go now, not only when the tab closes.
    if (this.contentAccountId && !accountId && isSessionOnly()) wipeSessionOnly()
    this.accountGeneration++
    for (const store of this.transcripts.values()) store.lock()
    this.transcripts.clear()
    this.hub.resetAccount()
    this.content?.invalidate()
    this.entities?.lock()
    this.entities =
      accountId && this.content
        ? new BrowserEntities({
            accountId,
            keyForEpoch: (epoch) => this.content!.readKey(accountId, epoch),
            currentKey: async () => {
              const current = await this.content!.currentKey()
              if (current.accountId !== accountId) throw new Error('Encrypted account changed')
              return current
            },
            transport: entityTransport(this.fetcher, API_ORIGIN),
            // ---- l-fresh: fresh-account empty state ----
            unprovisioned: () => this.content!.unprovisioned(accountId),
            // ---- end l-fresh ----
          })
        : undefined
    const entities = this.entities
    this.privateWork = entities
      ? new PrivateWork(
          async () => {
            if (this.entities !== entities) throw new Error('Private work account changed')
            return entities
          },
          () => this.api.machines(),
          () => this.api.runs(),
        )
      : undefined
    this.privateHistory?.lock()
    this.privateHistory =
      accountId && this.content
        ? new PrivateHistory(
            new BrowserHistory({
              accountId,
              keyForEpoch: (account, epoch) => this.content!.readKey(account, epoch),
              transport: historyTransport(this.fetcher, API_ORIGIN),
            }),
          )
        : undefined
    this.privateRuns?.lock()
    this.privateRuns =
      accountId && this.content
        ? new BrowserRuns(
            accountId,
            (command, out) => this.content!.outcome(command, out),
            (command) => this.content!.requestPayload(command),
          )
        : undefined
    // ---- materialized-state: per-account snapshot index + sealed local material cache ----
    this.runSnapshots?.lock()
    this.materialCache?.lock()
    this.runSnapshots = undefined
    this.materialCache = undefined
    if (accountId && this.content) {
      const content = this.content
      const snapshots = new RunSnapshots({
        accountId,
        keyForEpoch: (epoch) => content.readKey(accountId, epoch),
        request: (runIds) => this.hub.runSnapshots(runIds),
      })
      snapshots.subscribe((snap) => {
        this.transcripts.get(snap.runId)?.seed(snap.material)
        this.changedRunsSoon()
      })
      this.runSnapshots = snapshots
      this.materialCache = new MaterialCache(accountId, {
        async current() {
          const state = await content.vault.state(accountId)
          const epoch = Math.max(0, ...(state?.epochs ?? []))
          if (!epoch) return undefined
          return { epoch, key: await content.vault.epochKey(accountId, epoch) }
        },
        forEpoch: (epoch) => content.vault.epochKey(accountId, epoch),
      })
    }
    // ---- end materialized-state ----
    this.changedRuns()
    this.artifacts?.lock()
    this.artifacts = undefined
    this.contentAccountId = accountId
    if (this.content && accountId)
      this.artifacts = new BrowserArtifacts({
        accountId,
        vault: { epochKey: (account, epoch) => this.content!.readKey(account, epoch) },
        currentKey: async () => {
          const key = await this.content!.currentKey()
          if (key.accountId !== accountId) throw new Error('Encrypted account changed')
          return key
        },
        transport: browserArtifactTransport(
          (input, init) =>
            this.fetcher(
              typeof input === 'string' ? input : input instanceof URL ? input.href : input.url,
              init,
            ),
          API_ORIGIN,
        ),
        // ---- screen-sweep: sealed artifact index entry for the Artifacts tab ----
        ...(entities ? { index: (entry) => recordArtifactIndex(entities, entry) } : {}),
        // ---- end screen-sweep ----
      })
    for (const listener of this.accountListeners) listener()
  }
  transcriptStore(runId: string, accountId = this.contentAccountId ?? '') {
    if (accountId !== (this.contentAccountId ?? '')) throw new Error('Transcript account changed')
    let store = this.transcripts.get(runId)
    if (!store) {
      store = new RunStore(runId, this.storeDeps(runId)) // materialized-state
      this.transcripts.set(runId, store)
      const observed = store
      let status = store.snapshot.status
      let cost = store.snapshot.usage?.costUsd // usage-title: list rows follow cost changes
      store.subscribe(() => {
        const nextCost = observed.snapshot.usage?.costUsd
        if (nextCost !== cost) {
          cost = nextCost
          this.changedRuns()
        }
        if (observed.snapshot.status === status) return
        status = observed.snapshot.status
        this.changedRuns()
        // ---- b-work-web: clear the mirrored run claim once this device sees it stop ----
        if (status === 'idle' || status === 'error') {
          const work = this.privateWork
          void work
            ?.recordRunEnded(runId, status === 'error' ? 'failed' : 'finished')
            .then(() => this.changedRuns())
            .catch(() => {})
        }
        // ---- end b-work-web ----
      })
    }
    return store
  }
  // ---- materialized-state: snapshot + tail stores; snapshot-only list views ----
  runSnapshots: RunSnapshots | undefined
  private materialCache: MaterialCache | undefined
  private changedSoon: ReturnType<typeof setTimeout> | undefined
  /** Snapshot pushes arrive about once a second per busy run: re-list at most once a second. */
  private changedRunsSoon() {
    if (this.changedSoon) return
    this.changedSoon = setTimeout(() => {
      this.changedSoon = undefined
      this.changedRuns()
    }, 1000)
  }
  private storeDeps(runId: string): ConstructorParameters<typeof RunStore>[1] {
    const snapshots = this.runSnapshots
    const cache = this.materialCache
    if (!snapshots || !this.content) return {}
    return {
      ...(cache
        ? {
            cache: {
              load: async () => {
                const c = await cache.load(runId)
                // A sealed local state pins the run: an older relay snapshot is a rollback.
                if (c) snapshots.pin(runId, c.seq, c.epoch)
                return c
              },
              save: (material) => cache.save(runId, material),
            },
          }
        : {}),
      snapshot: async () => {
        await snapshots.fetch([runId], { force: true })
        return snapshots.get(runId)
      },
      readPage: (after) => this.hub.eventPage(runId, after),
    }
  }
  /** A run's current view: its open store, else its daemon snapshot (list surfaces). */
  runView(runId: string): TranscriptView | undefined {
    const open = this.transcripts.get(runId)?.snapshot
    if (open?.lastSeq) return open
    const snap = this.runSnapshots?.get(runId)
    return snap ? materialView(snap.material) : open
  }
  // ---- end materialized-state ----
  async forgetContent() {
    const accountId = this.contentAccountId
    await this.privateHistory?.forget()
    await this.privateRuns?.forget()
    this.bindAccount(undefined)
    if (accountId) await this.content?.vault.forget(accountId)
  }
  get artifactAccess() {
    return {
      accountId: this.contentAccountId ?? '',
      required: !!this.content,
      ...(this.artifacts ? { reader: this.artifacts } : {}),
    }
  }
  async uploadAttachments(files: PendingFile[], runId: string, machineId: string) {
    if (this.content) {
      await this.content.currentKey(machineId)
      if (!this.artifacts) throw new Error('Encrypted content locked')
    }
    return uploadAll(this.api, this.fetcher, files, runId, this.artifacts)
  }

  /** Sign and deliver. Resolves with the hub/daemon outcome; throws on rejection. */
  async dispatch<K extends CommandKind>(
    kind: K,
    machineId: string,
    payload: CommandPayload<K>,
    opts: SignOptions = {},
  ): Promise<CommandOutcome> {
    const accountId = await this.content?.accountId()
    if (this.content && kind === 'run.start') {
      const prepared = await (await this.workForAccount()).prepareRun(
        machineId,
        payload as CommandPayload<'run.start'>,
      )
      payload = prepared as CommandPayload<K>
    }
    const signed = await this.signer.sign(kind, machineId, payload, opts)
    if (accountId && EncryptedContent.parse(signed.payload).context.accountId !== accountId)
      throw new Error('Command account changed')
    const out = await this.deliver(signed)
    // passkey-budget WP0: allowing a secret goes through the risky secret.approve (a passkey).
    const secretAllow = secretApprovalRetry(kind, payload, out)
    if (secretAllow)
      return this.dispatch('secret.approve', machineId, secretAllow, {
        summary: SECRET_APPROVAL_SUMMARY,
      })
    // passkey-budget WP6: a grant never covers protected paths; land with the passkey instead.
    const tapped = grantProtectedRetry(kind, opts, out)
    if (tapped) return this.dispatch(kind, machineId, payload, tapped)
    if (out.status === 'rejected' || out.status === 'failed') throw new CommandRejected(out, kind)
    // ---- b-work-web: mirror the started/rotated run for other trusted devices ----
    if (this.content && kind === 'run.start') {
      const work = this.privateWork,
        start = payload as CommandPayload<'run.start'>,
        result = out.result as { status?: unknown } | undefined
      void work
        ?.recordRunStarted(
          start,
          machineId,
          typeof result?.status === 'string' ? result.status : 'running',
        )
        .then(() => this.changedRuns())
        .catch(() => {})
    }
    // ---- end b-work-web ----
    return out
  }

  // ---- live-activity: user rename of a run, sealed as a `runtitle` entity ----
  readonly runTitles = new RunTitles()
  async renameRun(runId: string, title: string) {
    const entities = this.entities
    if (!entities) throw new Error('Renaming needs an unlocked encrypted account')
    await this.runTitles.rename(entities, runId, title)
    this.changedRuns()
  }
  // ---- end live-activity ----

  // ---- bark-triage: the signed project binding a bark.submit carries (same as run.start) ----
  async barkBinding(machineId: string, projectId: string) {
    const prepared = await (await this.workForAccount()).prepareRun(machineId, {
      runId: 'bark-binding',
      agent: 'claude',
      prompt: '',
      projectId,
    })
    return prepared.privateProject
  }
  // ---- end bark-triage ----

  /**
   * Sign a command the hub prepared (resume, pick reply, rotation, brain job). The hub cannot
   * sign; this device does, and risky kinds still take the step-up tap.
   */
  signPrepared(cmd: PreparedCommand, opts: SignOptions = {}): Promise<CommandOutcome> {
    if (!cmd.machineId) throw new Error('No machine can run this yet.')
    return this.dispatch(cmd.kind as CommandKind, cmd.machineId, cmd.payload as never, opts)
  }

  /**
   * Socket first; HTTP twin when the socket is down; outbox (same envelope) if both fail.
   * Re-delivering the same SignedCommand is idempotent by `envelope.id`.
   */
  async deliver(signed: SignedCommand): Promise<CommandOutcome> {
    const decode = async (out: CommandOutcome) => {
      if (!this.content) return out
      if (out.status === 'duplicate') {
        const saved = await this.api.commandStatus(signed.envelope.id)
        out = saved
      }
      if (out.status === 'accepted' || out.status === 'delivered' || out.status === 'queued')
        out = await this.hub.send(signed)
      const opened = await this.content.outcome(signed, out)
      const runs = this.privateRuns
      await runs?.remember(signed, out)
      if (runs && runs === this.privateRuns) this.changedRuns()
      return opened
    }
    if (this.hub.connection === 'live') {
      try {
        return await decode(await this.hub.send(signed))
      } catch (e) {
        if (!(e instanceof CommandTimeoutError)) throw e
      }
    }
    try {
      return await decode(await this.api.submitCommand(signed))
    } catch (e) {
      if (e instanceof ApiError && e.status === 0) return decode(await this.hub.send(signed))
      throw e
    }
  }
}

export class CommandRejected extends Error {
  constructor(
    readonly outcome: CommandOutcome,
    readonly kind: string,
  ) {
    super(rejectMessage(outcome))
  }
}

export function rejectMessage(o: CommandOutcome): string {
  switch (o.reason) {
    case 'missing-step-up':
    case 'bad-step-up':
      return 'This action needs a fresh passkey confirmation.'
    case 'unknown-device':
    case 'bad-endorsement':
      return 'This machine does not trust this device yet. Re-trust it from You › Devices.'
    case 'revoked':
      return 'This device was revoked. Sign in again to trust it.'
    case 'expired':
    case 'not-yet-valid':
      return 'The command expired before it reached the machine. Check this device’s clock and retry.'
    case 'machine-mismatch':
      return 'That command was addressed to a different machine.'
    default:
      return o.error ?? `The machine refused the command${o.reason ? ` (${o.reason})` : ''}.`
  }
}

/** `MOCK` is a build-time constant, so production bundles drop the mock (and fixtures). */
export async function createBackend(): Promise<Backend> {
  // Inline literal (not the MOCK re-export) so the bundler drops the mock chunk entirely.
  if (import.meta.env.VITE_VIKO_MOCK === '1') {
    const { createMockBackend } = await import('../mock/index.js')
    const m = createMockBackend()
    return new Backend(m.fetcher, m.socket, false, m.lock)
  }
  return new Backend((i, init) => fetch(i, init), browserSocket(API_ORIGIN))
}

export const BackendContext = createContext<Backend | null>(null)

export function useBackend(): Backend {
  const b = useContext(BackendContext)
  if (!b) throw new Error('BackendContext missing')
  useSyncExternalStore(b.subscribeContent ?? (() => () => {}), b.contentVersion ?? (() => 0))
  return b
}
